import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/shared/providers/AuthContext';
import AppButton from '@/components/atoms/AppButton';
import AppTextField from '@/components/atoms/AppTextField';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import VisitClinicPatientSearchModal from '@/pages/main/visitClinic/components/VisitClinicPatientSearchModal';
import { useInfinitePatientSearch } from '@/hooks/main/usePatientSearch';
import { useVisitClinicYearmonthSearch } from '@/hooks/main/useVisitClinic';
import useAppModal from '@/hooks/useAppModal';
import { isoMonthToJaWareki } from '@/utils/dateConverter';
import { findExactSerialNumberMatch } from '@/pages/main/patient/utils/patientSerialNumberMatch';

interface Props {
  /**
   * If provided, shows the 施術年月 month picker next to the search button.
   * Format: YYYY-MM (from URL params on the edit page).
   * When not provided (list page), the month picker is hidden.
   */
  yearmonth?: string;
  /** Called when the user selects a new month. Parent handles navigation. */
  onYearmonthChange?: (ym: string) => void;
}

/**
 * Shared search bar for VisitClinicSchedulePage and VisitClinicEditPage.
 *
 * Search flow:
 *   1. User types keyword and clicks 検索 (or presses Enter)
 *   2. Full-page spinner shown while fetching
 *   3. 0 results → window.alert, no modal
 *   4. A result whose 受診者番号 equals the keyword → auto-navigate to its edit page, no modal
 *      (HIT-14771: 「1」 opens 受診者番号 1 even when 10, 11… also match)
 *   5. 1 result + numeric keyword → auto-navigate to edit page, no modal
 *   6. Otherwise (several results, or 1 result + non-numeric) → open patient list modal
 */
const VisitClinicSearchBar: React.FC<Props> = ({ yearmonth, onYearmonthChange }) => {
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  // submittedKeyword is set when user triggers search; null = no search in flight
  const [submittedKeyword, setSubmittedKeyword] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // BD 1.2.2 §4.4/4.5: ＜前月 / 翌月＞ are disabled while the 施術年月 field is empty. The picker
  // reports a cleared field as '' but the page keeps the previous month, so track it here.
  const [isYearmonthEmpty, setIsYearmonthEmpty] = useState(false);
  useEffect(() => {
    setIsYearmonthEmpty(false);
  }, [yearmonth]);

  const { clinic } = useAuth();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const modal = useAppModal();

  const siteType = clinic?.site_type ?? '';
  const clinicNumber = clinic?.clinic_number ?? '';

  const yearmonthSearch = useVisitClinicYearmonthSearch();

  const filters = useMemo(
    () =>
      submittedKeyword !== null
        ? {
            keyword: submittedKeyword,
            includeExcluded: false,
            sortField: 'serial_number',
            sortOrder: 'asc' as const,
            officialOnly: true,
          }
        : null,
    [submittedKeyword]
  );

  const {
    data: searchData,
    isPending,
    isError,
  } = useInfinitePatientSearch(siteType, clinicNumber, filters, {
    enabled: submittedKeyword !== null,
  });

  // Decide: alert / auto-navigate / open modal once preflight fetch completes
  useEffect(() => {
    if (submittedKeyword === null || isPending) return;
    if (searchData === undefined && !isError) return;

    if (isError) {
      // Let the modal show the error UI
      setIsModalOpen(true);
      setSubmittedKeyword(null);
      return;
    }

    const pages = searchData?.pages ?? [];
    const patients = pages.flatMap(page => page.data ?? []);
    const total = pages[0]?.pagination?.total ?? patients.length;
    const isNumeric = /^\d+$/.test(submittedKeyword.trim());

    if (total === 0) {
      modal.warning({
        title: '受診者が見つかりませんでした。',
        okText: 'OK',
      });
      setSubmittedKeyword(null);
      return;
    }

    // HIT-14771: an exact 受診者番号 match wins over the other partial matches (A2/R2 matched
    // 受診者番号 with `=`); the API sorts it first, so it is always on the first page.
    const exactSerialNumberMatch = findExactSerialNumberMatch(patients, submittedKeyword);
    if (exactSerialNumberMatch || (total === 1 && isNumeric)) {
      const p = exactSerialNumberMatch ?? patients[0];
      const patientId = p.patient?.patient_id ?? p.id ?? 0;
      const navigateToEdit = (ym: string) => {
        const jaYearmonth = isoMonthToJaWareki(ym);
        navigate(`/${tenant}/visit_clinic/edit/${patientId}/${jaYearmonth}`);
      };
      setSubmittedKeyword(null);
      yearmonthSearch.mutate(patientId, {
        onSuccess: ({ yearmonth: ym }) => {
          navigateToEdit(ym);
        },
        onError: () => {
          navigateToEdit(dayjs().format('YYYY-MM'));
        },
      });
      return;
    }

    // Multiple results (or 1 result with non-numeric keyword) → open modal
    setIsModalOpen(true);
    setSubmittedKeyword(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedKeyword, isPending, searchData, isError, modal, navigate, tenant, yearmonthSearch]);

  const handleSearch = () => {
    if (submittedKeyword !== null || yearmonthSearch.isPending) return; // already searching
    setSearchKeyword(keyword);
    setSubmittedKeyword(keyword);
    setKeyword('');
  };

  const isLoading = (submittedKeyword !== null && isPending) || yearmonthSearch.isPending;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <AppTextField
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="受診者氏名"
          className="w-48"
          maxLength={255}
        />
        <AppButton type="primary" onClick={handleSearch} loading={isLoading} disabled={isLoading}>
          検索
        </AppButton>

        {/* 施術年月: only shown on the edit page (when yearmonth is provided) */}
        {yearmonth !== undefined && (
          <>
            <span className="whitespace-nowrap text-sm ml-2">施術年月</span>
            <RA2NDatePicker
              picker="month"
              value={isYearmonthEmpty ? '' : yearmonth}
              endDate={new Date()}
              placeholder="年月指定"
              onChange={val => {
                setIsYearmonthEmpty(!val);
                if (val && onYearmonthChange) {
                  onYearmonthChange(val);
                }
              }}
              className="w-36"
            />
            <AppButton
              type="primary"
              disabled={isYearmonthEmpty || !yearmonth}
              onClick={() => {
                if (onYearmonthChange && yearmonth) {
                  onYearmonthChange(dayjs(yearmonth).subtract(1, 'month').format('YYYY-MM'));
                }
              }}
            >
              ＜前月
            </AppButton>
            <AppButton
              type="primary"
              disabled={
                isYearmonthEmpty ||
                !yearmonth ||
                dayjs(yearmonth).format('YYYY-MM') >= dayjs().format('YYYY-MM')
              }
              onClick={() => {
                if (onYearmonthChange && yearmonth) {
                  onYearmonthChange(dayjs(yearmonth).add(1, 'month').format('YYYY-MM'));
                }
              }}
            >
              翌月＞
            </AppButton>
          </>
        )}
      </div>

      <VisitClinicPatientSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        keyword={searchKeyword}
      />
    </>
  );
};

export default VisitClinicSearchBar;
